import { components } from "@/api-schema";
import { DeviceType } from "@/common/hooks/use-device";
import { BrandLogo } from "@/components/brand-logo/brand-logo";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { ImplementedAccessorColumnDef } from "@/components/data-table/types";
import { EmptyValue } from "@/components/ui/empty-value";
import { renderBrandType } from "@/modules/masterdata/brand/_helpers/type-renderer";
import { getBrandDatIdentifier, getBrandV1Identifier } from "@/modules/masterdata/brand/brand-identifiers";
import i18next from "i18next";
import { commonColumns } from "./_constants/common-columns";

export type BrandTableType = components["schemas"]["Brand"];

type BrandName = BrandTableType["name"];
type BrandType = BrandTableType["type"];

export const brandTableDefaultColumns = {
  columnName: {
    id: "name",
    size: 100,
    enableSorting: true,
    align: "center",
    header: () => i18next.t("common:Name"),
    accessorFn: (row: BrandTableType) => row.name,
    cell: (cell) => {
      const brand = cell.row.original;
      return (
        <div className="flex flex-col items-center">
          <BrandLogo brand={brand} />
          <div>{automaticCellRenderer(brand.name)}</div>
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<BrandTableType, BrandName>,

  columnTechnicalId: commonColumns.columnTechnicalId<BrandTableType>(),
  columnDatId: commonColumns.columnDatId<BrandTableType>(getBrandDatIdentifier),

  type: {
    id: "type",
    size: 100,
    enableSorting: true,
    header: () => i18next.t("masterdata:Type"),
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: BrandTableType) => row.type,
    cell: (cell) => automaticCellRenderer(renderBrandType(cell.getValue() as BrandType)),
  } as ImplementedAccessorColumnDef<BrandTableType, BrandType>,

  columnUuid: commonColumns.columnUuid<BrandTableType>(),
  columnV1Id: {
    id: "v1Id",
    size: 50,
    enableSorting: false,
    align: "right",
    header: () => i18next.t("masterdata:V1ID"),
    visibility: [],
    accessorFn: (row: BrandTableType) => getBrandV1Identifier(row),
    cell: (cell) => {
      const v1Identifier = cell.getValue();
      return v1Identifier ? automaticCellRenderer(v1Identifier) : <EmptyValue />;
    },
  } as ImplementedAccessorColumnDef<BrandTableType, string | undefined>,
  columnDeletedAt: commonColumns.columnDeletedAt<BrandTableType>(),
  columnCreatedAt: commonColumns.columnCreatedAt<BrandTableType>(),
  columnUpdatedAt: commonColumns.columnUpdatedAt<BrandTableType>(),
};
